import { useEffect, useState } from 'react'
import { useUserServiceAllTenantQuery } from '@/generated/graphql'
import { useGraphQLClient } from '@/libs/graphql-provider'
import { GraphQLError } from '@/types/graphql'
import { ChevronDown } from 'lucide-react'
import { useTenantStore } from '@/stores/tenantStore'
import { useTranslation } from '@/hooks/use-translation'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export function HeaderTenantSelector() {
  const { t } = useTranslation()
  const selectedTenantId = useTenantStore((state) => state.selectedTenantId)
  const setSelectedTenant = useTenantStore((state) => state.setSelectedTenant)
  const [currentPage, setCurrentPage] = useState(1)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [allTenants, setAllTenants] = useState<any[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const client = useGraphQLClient()
  const pageSize = 20

  // Calculate pagination parameters
  const skip = (currentPage - 1) * pageSize

  // Fetch tenants data
  const { data, error } = useUserServiceAllTenantQuery(
    client,
    {
      input: {
        limit: pageSize,
        skip: skip,
      },
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  )

  // Update tenants list when data changes
  useEffect(() => {
    if (data?.userServiceAllTenant) {
      if (currentPage === 1) {
        // First page - replace all tenants
        setAllTenants(data.userServiceAllTenant)

        // Auto-select first tenant if none is selected
        if (!selectedTenantId && data.userServiceAllTenant.length > 0) {
          const firstTenant = data.userServiceAllTenant[0]
          setSelectedTenant({
            id: firstTenant.id,
            name: firstTenant.name,
            miniAppUrl: firstTenant.miniAppUrl,
            type: firstTenant.type,
          })
        }
      } else {
        // Subsequent pages - append to existing tenants
        setAllTenants((prev) => [...prev, ...data.userServiceAllTenant])
      }

      // Check if there are more pages
      setHasMore(data.userServiceAllTenant.length === pageSize)
      setIsLoadingMore(false)
    }
  }, [data, currentPage, selectedTenantId, setSelectedTenant])

  const loadMoreTenants = () => {
    if (hasMore && !isLoadingMore) {
      setIsLoadingMore(true)
      setCurrentPage((prev) => prev + 1)
    }
  }

  const handleTenantChange = (tenantId: string) => {
    const selectedTenantData = allTenants.find(
      (tenant) => tenant.id === tenantId
    )
    if (selectedTenantData) {
      setSelectedTenant({
        id: selectedTenantData.id,
        name: selectedTenantData.name,
        miniAppUrl: selectedTenantData.miniAppUrl,
        type: selectedTenantData.type,
      })
    }
  }

  if (error) {
    return (
      <div className='text-sm text-destructive'>
        {t('errors.loadingTenants', {
          message: (error as GraphQLError).message,
        })}
      </div>
    )
  }

  return (
    <div>
      <div className='flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 outline-none ring-sidebar-ring transition-[margin,opa] duration-200 ease-linear focus-visible:ring-2 group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0 [&>svg]:size-4 [&>svg]:shrink-0'>
        {t('common.tenant')}
      </div>
      <Select value={selectedTenantId || ''} onValueChange={handleTenantChange}>
        <SelectTrigger id='header-tenant-select' className='w-full'>
          <SelectValue placeholder={t('common.tenant')} />
        </SelectTrigger>
        <SelectContent>
          {allTenants.map((tenant) => (
            <SelectItem key={tenant.id} value={tenant.id}>
              {tenant.name}
            </SelectItem>
          ))}
          {hasMore && (
            <div className='p-2'>
              <Button
                variant='ghost'
                size='sm'
                onClick={loadMoreTenants}
                disabled={isLoadingMore}
                className='w-full'
              >
                {isLoadingMore ? (
                  t('common.loading')
                ) : (
                  <>
                    <ChevronDown className='mr-2 h-4 w-4' />
                    {t('common.loadMore', 'Load More')}
                  </>
                )}
              </Button>
            </div>
          )}
        </SelectContent>
      </Select>
    </div>
  )
}
